/**
 * useEvaluations — data hook for the tutor grades grid.
 *
 * Handles:
 *  - Loading evaluations, config, and bulletin for a given contract/subject/period.
 *  - Saving (create or update) a single evaluation cell.
 *  - Upserting the weighting config.
 *  - Refreshing the bulletin after every successful save.
 */

import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';

/**
 * @param {Object} params
 * @param {string|number|null} params.contractId
 * @param {string|number|null} params.subjectId
 * @param {string|number|null} params.periodId
 * @param {string|number|null} params.studentId
 */
export function useEvaluations({ contractId, subjectId, periodId, studentId }) {
  const [evaluations, setEvaluations] = useState([]);
  const [config, setConfig] = useState(null);
  const [bulletin, setBulletin] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingBulletin, setLoadingBulletin] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const ready = contractId && subjectId && periodId && studentId;

  // Load evaluations + config when selectors are set
  useEffect(() => {
    if (!ready) {
      setEvaluations([]);
      setConfig(null);
      setBulletin(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoadingData(true);
      setLoadError(null);
      try {
        const [evalRes, configRes] = await Promise.all([
          apiService.getEvaluations(contractId, { subject_id: subjectId, period_id: periodId }),
          apiService.getEvaluationConfig(contractId),
        ]);
        if (cancelled) return;
        // API returns { data: [...] } or plain array
        setEvaluations(evalRes?.data ?? evalRes ?? []);
        setConfig(configRes?.data ?? configRes ?? null);
      } catch (err) {
        if (!cancelled) setLoadError(err);
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [contractId, subjectId, periodId, ready]);

  // Load bulletin when selectors are set
  const refreshBulletin = useCallback(async () => {
    if (!studentId || !periodId) return;
    setLoadingBulletin(true);
    try {
      const res = await apiService.getBulletin(studentId, periodId);
      setBulletin(res?.data ?? res ?? null);
    } catch {
      // Bulletin is a read-model — best-effort; don't block the grid on failure
    } finally {
      setLoadingBulletin(false);
    }
  }, [studentId, periodId]);

  useEffect(() => {
    if (ready) refreshBulletin();
  }, [ready, refreshBulletin]);

  /**
   * Save a single evaluation cell (create or update).
   * Returns { ok: true } or { ok: false, fieldErrors, userMessage }.
   */
  const saveCell = useCallback(async ({ evaluationId, type, score, label, evaluatedOn }) => {
    try {
      let saved;
      if (evaluationId) {
        const res = await apiService.updateEvaluation(contractId, evaluationId, {
          score,
          label,
          evaluated_on: evaluatedOn,
        });
        saved = res?.data ?? res;
        // Update existing entry in the list
        setEvaluations((prev) =>
          prev.map((e) => (e.id === evaluationId ? saved : e))
        );
      } else {
        const res = await apiService.saveEvaluation(contractId, {
          subject_id: subjectId,
          period_id: periodId,
          type,
          score,
          label,
          evaluated_on: evaluatedOn,
        });
        saved = res?.data ?? res;
        setEvaluations((prev) => [...prev, saved]);
      }
      await refreshBulletin();
      return { ok: true };
    } catch (err) {
      const { getFieldErrors, getUserMessage } = await import('../utils/apiErrors');
      return {
        ok: false,
        fieldErrors: getFieldErrors(err),
        userMessage: getUserMessage(err),
      };
    }
  }, [contractId, subjectId, periodId, refreshBulletin]);

  /**
   * Upsert weighting config.
   * Returns { ok: true, config } or { ok: false, fieldErrors, userMessage }.
   */
  const saveConfig = useCallback(async (data) => {
    try {
      const res = await apiService.updateEvaluationConfig(contractId, {
        subject_id: subjectId,
        period_id: periodId,
        ...data,
      });
      const saved = res?.data ?? res;
      setConfig(saved);
      await refreshBulletin();
      return { ok: true, config: saved };
    } catch (err) {
      const { getFieldErrors, getUserMessage } = await import('../utils/apiErrors');
      return {
        ok: false,
        fieldErrors: getFieldErrors(err),
        userMessage: getUserMessage(err),
      };
    }
  }, [contractId, subjectId, periodId, refreshBulletin]);

  return {
    evaluations,
    config,
    bulletin,
    loadingData,
    loadingBulletin,
    loadError,
    saveCell,
    saveConfig,
    refreshBulletin,
  };
}

export default useEvaluations;
